import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MapPin, Eye, Plus, Pencil, Trash2, Check, X, 
  TrendingUp, Search, ToggleLeft, ToggleRight 
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCityPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    slug: '',
    city_name: '',
    meta_title: '',
    meta_description: '',
    h1_title: '',
    intro_text: '',
    services_text: '',
    local_text: '',
    cta_text: '',
    is_active: true
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('jardily_token');
      const res = await axios.get(`${API}/city-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(res.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Erreur lors du chargement des pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('jardily_token');

    try {
      if (editingPage) {
        await axios.put(`${API}/city-pages/${editingPage.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Page mise à jour avec succès');
      } else {
        await axios.post(`${API}/city-pages`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Page créée avec succès');
      }
      setIsDialogOpen(false);
      setEditingPage(null);
      resetForm();
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      slug: page.slug,
      city_name: page.city_name,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
      h1_title: page.h1_title,
      intro_text: page.intro_text,
      services_text: page.services_text,
      local_text: page.local_text,
      cta_text: page.cta_text,
      is_active: page.is_active
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette page ?')) return;
    
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.delete(`${API}/city-pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page supprimée');
      fetchPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (page) => {
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.put(`${API}/city-pages/${page.id}`, { is_active: !page.is_active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(page.is_active ? 'Page désactivée' : 'Page activée');
      fetchPages();
    } catch (error) {
      console.error('Error toggling page:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      city_name: '',
      meta_title: '',
      meta_description: '',
      h1_title: '',
      intro_text: '',
      services_text: '',
      local_text: '',
      cta_text: '',
      is_active: true
    });
  };

  const generateSlug = (cityName) => {
    return `jardinier-${cityName.toLowerCase().replace(/['\s]/g, '-').replace(/[éèê]/g, 'e').replace(/[àâ]/g, 'a')}`;
  };

  const handleCityNameChange = (value) => {
    setFormData({
      ...formData,
      city_name: value,
      slug: generateSlug(value),
      meta_title: `Jardinier à ${value} - Entretien de jardin | Jardily`,
      h1_title: `Votre jardinier à ${value}`
    });
  };

  const filteredPages = pages.filter(page => 
    page.city_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalViews = pages.reduce((sum, page) => sum + page.view_count, 0);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl text-stone-900">Pages SEO Villes</h1>
            <p className="text-stone-600 text-sm mt-1">
              Gérez vos pages de référencement local
            </p>
          </div>
          <Button 
            onClick={() => { resetForm(); setEditingPage(null); setIsDialogOpen(true); }}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle page
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-stone-600 text-sm">Total pages</span>
            </div>
            <p className="font-heading font-bold text-3xl text-stone-900">{pages.length}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-stone-600 text-sm">Total vues</span>
            </div>
            <p className="font-heading font-bold text-3xl text-stone-900">{totalViews}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-stone-600 text-sm">Pages actives</span>
            </div>
            <p className="font-heading font-bold text-3xl text-stone-900">
              {pages.filter(p => p.is_active).length}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une ville..."
              className="pl-10 rounded-xl"
            />
          </div>
        </div>

        {/* Pages Table */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-600">Aucune page trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-stone-700">Ville</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-stone-700">Slug</th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-stone-700">Vues</th>
                    <th className="text-center py-4 px-6 text-sm font-semibold text-stone-700">Statut</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-stone-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredPages.map((page) => (
                    <tr key={page.id} className="hover:bg-stone-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-emerald-600" />
                          </div>
                          <span className="font-medium text-stone-900">{page.city_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <a 
                          href={`/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-700 text-sm"
                        >
                          /{page.slug}
                        </a>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          <Eye className="w-4 h-4" />
                          {page.view_count}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() => toggleActive(page)}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                            page.is_active 
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' 
                              : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                          }`}
                        >
                          {page.is_active ? (
                            <>
                              <ToggleRight className="w-4 h-4" />
                              Actif
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4" />
                              Inactif
                            </>
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(page)}
                            className="hover:bg-emerald-50"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(page.id)}
                            className="hover:bg-red-50 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingPage ? 'Modifier la page' : 'Nouvelle page ville'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la ville *</Label>
                <Input
                  value={formData.city_name}
                  onChange={(e) => handleCityNameChange(e.target.value)}
                  placeholder="ex: Istres"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Slug URL *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="ex: jardinier-istres"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meta Title (SEO) *</Label>
              <Input
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder="Titre pour les moteurs de recherche"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Meta Description (SEO) *</Label>
              <Textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Description pour les moteurs de recherche (150-160 caractères)"
                rows={2}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Titre H1 *</Label>
              <Input
                value={formData.h1_title}
                onChange={(e) => setFormData({ ...formData, h1_title: e.target.value })}
                placeholder="Titre principal de la page"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Texte d'introduction (HTML) *</Label>
              <Textarea
                value={formData.intro_text}
                onChange={(e) => setFormData({ ...formData, intro_text: e.target.value })}
                placeholder="<p>Introduction...</p>"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Section services (HTML) *</Label>
              <Textarea
                value={formData.services_text}
                onChange={(e) => setFormData({ ...formData, services_text: e.target.value })}
                placeholder="<h2>Nos services...</h2>"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Section locale (HTML) *</Label>
              <Textarea
                value={formData.local_text}
                onChange={(e) => setFormData({ ...formData, local_text: e.target.value })}
                placeholder="<h2>Pourquoi nous choisir...</h2>"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Texte CTA *</Label>
              <Input
                value={formData.cta_text}
                onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                placeholder="Demandez votre devis gratuit..."
                required
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Page active</Label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button type="submit" className="btn-primary">
                {editingPage ? 'Enregistrer' : 'Créer la page'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCityPages;
